<template>
  <div class="pb-3">
    <template v-if="hasGroups">
      <DeviceGroupBar
        v-model:selected="view"
        class="mb-3"
        :devices
        :countByGroup
        :groups
        :suggestions
        :busy
        @createSuggestion="createFromSuggestion"
        @dismissSuggestion="dismiss"
      />
      <DeviceGroupActionBar
        :countByGroup
        :selectedDevices
        :groups
        :viewedGroup
        :busy
        @addToGroup="addSelection"
        @newGroupFromSelection="modal?.openCreate(selectedIds)"
        @removeFromGroup="removeSelection"
        @clearSelection="selectedIds = []"
        @editGroup="modal?.openEdit($event)"
      />
    </template>
    <ProductionDevicesTable
      v-model:selection="selectedIds"
      :companies="[{ ...company, id: company.id! }]"
      :registryDevices="devicesInView"
      :companyId="company.id!"
      :ltReportingStatuses
      :showScheduledTransactions="true"
      :showCompanyColumn="false"
      :selectable="hasGroups"
      :compactIdentity="hasGroups"
      :showGroupColumn="hasGroups && view === 'all'"
      :lockedBy
    />
    <DeviceGroupModal
      ref="modal"
      :companyId="company.id!"
      :devices
      :suggestions
      @saved="view = $event"
    />
  </div>
</template>

<script setup lang="ts">
import ProductionDevicesTable from "@/dashboard/consolidated/ProductionDevicesTable.vue";
import type { LtReportingStatusMap } from "@/dashboard/consolidated/ProductionDevicesTable.vue";
import DeviceGroupActionBar from "@/dashboard/production_strategy/device_groups/DeviceGroupActionBar.vue";
import DeviceGroupBar from "@/dashboard/production_strategy/device_groups/DeviceGroupBar.vue";
import DeviceGroupModal from "@/dashboard/production_strategy/device_groups/DeviceGroupModal.vue";
import { isInOtherGroup } from "@/dashboard/production_strategy/device_groups/deviceGroupUtils";
import { useDeviceGroupWorkspace } from "@/dashboard/production_strategy/device_groups/useDeviceGroupWorkspace";
import type { Company, RegistryDevice } from "@/types";
import { useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Devices tab's device table and, once the company has a group, its group chips and selection
 * bar above it. Owns the group modal; the tab header opens it through `openCreate`.
 */
const props = defineProps<{
  company: Company;
  /** The devices the tab shows, i.e. without the company's excluded ones. */
  devices: RegistryDevice[];
  ltReportingStatuses?: LtReportingStatusMap;
}>();

const { t } = useI18n();
const modal = useTemplateRef<InstanceType<typeof DeviceGroupModal>>("modal");

const {
  groups,
  hasGroups,
  view,
  viewedGroup,
  devicesInView,
  selectedIds,
  selectedDevices,
  countByGroup,
  busy,
  suggestions,
  addSelection,
  removeSelection,
  createFromSuggestion,
  dismiss,
} = useDeviceGroupWorkspace(
  () => props.company.id!,
  () => props.devices
);

// In All and Ungrouped the selection feeds "Add to group", and a device is in at most one group, so
// a grouped device is locked there; in a group's own view every member can be picked for removal.
const lockedBy = (device: RegistryDevice): string | null =>
  isInOtherGroup(device, viewedGroup.value?.id ?? null)
    ? t("device_groups.in_group", { group: device.device_group_name ?? "" })
    : null;

/** Opens an empty "New group". */
const openCreate = () => modal.value?.openCreate();

defineExpose({ openCreate });
</script>
